<script lang="ts">
  import { confirmPersonName, recordMarketingConsent } from '../../lib/convexApi';
  import { session, markNameConfirmed, markConsentRecorded } from '../session';
  import { gotoHome } from '../router';
  import Notice from '../ui/Notice.svelte';

  // Name: prefilled with the current confirmed name (session displayName). Save
  // re-calls persons:confirmName (self-only, re-callable) — a rename restamps.
  let name = $session.displayName ?? '';
  let nameSaving = false;
  let nameError: string | null = null;
  let nameSaved = false;

  // Keep the field in sync with the session until the user edits it, so a late
  // sync (or a save elsewhere) doesn't leave a stale prefill.
  let namePrefill = $session.displayName ?? '';
  $: if (namePrefill !== ($session.displayName ?? '') && !nameSaving && name === namePrefill) {
    name = $session.displayName ?? '';
    namePrefill = $session.displayName ?? '';
  }

  $: trimmedName = name.trim();
  $: nameUnchanged = trimmedName === ($session.displayName ?? '').trim();
  $: nameSaveDisabled = nameSaving || trimmedName === '' || nameUnchanged;

  async function saveName() {
    if (nameSaveDisabled) return;
    nameSaving = true;
    nameError = null;
    nameSaved = false;
    try {
      const person = await confirmPersonName(trimmedName);
      const confirmedAt =
        person && typeof person.nameConfirmedAt === 'number' ? person.nameConfirmedAt : Date.now();
      const confirmedName = person && typeof person.name === 'string' ? person.name : trimmedName;
      markNameConfirmed(confirmedAt, null, confirmedName);
      namePrefill = confirmedName;
      name = confirmedName;
      nameSaved = true;
    } catch (err) {
      nameError = err instanceof Error ? err.message : 'Could not save your name. Please try again.';
    } finally {
      nameSaving = false;
    }
  }

  // Updates: current marketing consent state, with the opposite action.
  $: consentGranted = $session.marketingConsentStatus === 'granted';
  let consentSaving = false;
  let consentError: string | null = null;

  async function setConsent(status: 'granted' | 'declined') {
    consentSaving = true;
    consentError = null;
    try {
      await recordMarketingConsent(status, 'settings');
      markConsentRecorded(status);
    } catch (err) {
      consentError = err instanceof Error ? err.message : 'Could not update your preference. Please try again.';
    } finally {
      consentSaving = false;
    }
  }
</script>

<div class="settings-page container">
  <button type="button" class="settings-page__back" on:click={() => gotoHome({ push: false })}>
    <span aria-hidden="true">←</span> Back to events
  </button>

  <header class="settings-page__header">
    <h1>Account settings</h1>
  </header>

  <section class="settings-card surface surface--raised" aria-labelledby="name-heading">
    <h2 id="name-heading">Your name</h2>
    <p class="settings-card__lede">Shows on guest lists and invites.</p>

    {#if nameError}
      <Notice variant="error">{nameError}</Notice>
    {:else if nameSaved}
      <Notice variant="success">Saved.</Notice>
    {/if}

    <form class="settings-card__form" on:submit|preventDefault={saveName}>
      <label class="settings-card__field">
        <span class="settings-card__label">Your name</span>
        <input
          class="settings-card__input"
          type="text"
          bind:value={name}
          on:input={() => (nameSaved = false)}
          maxlength="80"
          autocomplete="name"
          placeholder="First and last name"
          disabled={nameSaving}
        />
      </label>
      <button type="submit" class="accent" disabled={nameSaveDisabled}>
        {nameSaving ? 'Saving…' : 'Save'}
      </button>
    </form>
  </section>

  <section class="settings-card surface surface--raised" aria-labelledby="updates-heading">
    <h2 id="updates-heading">Updates</h2>

    {#if consentError}
      <Notice variant="error">{consentError}</Notice>
    {/if}

    {#if consentGranted}
      <p class="settings-card__lede">You're getting updates about events.</p>
      <button type="button" class="secondary" on:click={() => setConsent('declined')} disabled={consentSaving}>
        {consentSaving ? 'Saving…' : 'Stop sending me updates'}
      </button>
    {:else}
      <p class="settings-card__lede">You're not getting updates about events.</p>
      <button type="button" class="accent" on:click={() => setConsent('granted')} disabled={consentSaving}>
        {consentSaving ? 'Saving…' : 'Send me updates about events'}
      </button>
    {/if}
  </section>
</div>

<style>
  .settings-page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-4) var(--space-8);
    max-width: 34rem;
  }

  .settings-page__back {
    align-self: flex-start;
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    cursor: pointer;
    padding: 0;
  }

  .settings-page__back:hover {
    color: var(--color-text-secondary);
  }

  .settings-page__header h1 {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--font-size-xl);
    font-weight: 800;
  }

  .settings-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
    border: var(--border-subtle);
  }

  .settings-card h2 {
    margin: 0;
    font-size: var(--font-size-lg);
  }

  .settings-card__lede {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
  }

  .settings-card__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .settings-card__field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .settings-card__label {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }

  .settings-card__input {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-md);
  }

  .settings-card__form button,
  .settings-card button.accent,
  .settings-card button.secondary {
    align-self: flex-start;
  }
</style>
